import { API_URL } from '@/lib/config';
import { getAdminAuthHeaders, UnauthorizedError } from '@/lib/admin-api';

export type MediaUploadFolder = 'haber' | 'manset' | 'video' | 'icerik';

const MEDIA_ROOTS = new Set([
  'haber',
  'manset',
  'video',
  'icerik',
  'user-submissions',
  'storage',
]);

export function extractMediaPathFromUploadResponse(data: {
  url?: string;
  path?: string;
}): string {
  if (data.url && (data.url.startsWith('http://') || data.url.startsWith('https://'))) {
    const urlParts = data.url.split('/');
    const pathIndex = urlParts.findIndex((part) => MEDIA_ROOTS.has(part));
    if (pathIndex >= 0) {
      return urlParts.slice(pathIndex).join('/');
    }
    return data.path || '';
  }
  return data.path || data.url || '';
}

async function parseUploadError(response: Response, fallback: string): Promise<never> {
  if (response.status === 401 || response.status === 403) {
    throw new UnauthorizedError('Oturum süreniz dolmuş olabilir. Lütfen tekrar giriş yapın.');
  }
  const error = await response.json().catch(() => ({ message: fallback }));
  throw new Error(error.message || fallback);
}

export async function uploadMediaFile(
  file: File,
  folder: MediaUploadFolder = 'haber',
  nameHint?: string,
): Promise<string> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('folder', folder);
  if (nameHint?.trim()) {
    formData.append('name', nameHint.trim());
  }

  const response = await fetch(`${API_URL}/admin/media/upload`, {
    method: 'POST',
    headers: getAdminAuthHeaders(),
    body: formData,
    credentials: 'include',
  });

  if (!response.ok) {
    await parseUploadError(response, 'Yükleme başarısız');
  }

  const data = await response.json();
  return extractMediaPathFromUploadResponse(data);
}

export async function uploadDataUrlAsMedia(
  dataUrl: string,
  filename: string,
  folder: MediaUploadFolder = 'haber',
  nameHint?: string,
): Promise<string> {
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  const file = new File([blob], filename, { type: blob.type || 'image/jpeg' });
  return uploadMediaFile(file, folder, nameHint);
}

export async function uploadRemoteImageAsMedia(
  url: string,
  filename: string,
  folder: MediaUploadFolder = 'haber',
  nameHint?: string,
): Promise<string> {
  const response = await fetch(`${API_URL}/admin/media/fetch-upload`, {
    method: 'POST',
    credentials: 'include',
    headers: getAdminAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ url, folder, name: nameHint || filename }),
  });

  if (response.ok) {
    const data = await response.json();
    return extractMediaPathFromUploadResponse(data);
  }

  // Fallback: doğrudan istemciden indir (CORS izin verirse)
  const direct = await fetch(url);
  if (!direct.ok) {
    throw new Error('Uzak görsel indirilemedi');
  }
  const blob = await direct.blob();
  const ext = blob.type.includes('gif')
    ? 'gif'
    : blob.type.includes('webp')
      ? 'webp'
      : blob.type.includes('png')
        ? 'png'
        : 'jpg';
  const file = new File([blob], `${filename}.${ext}`, { type: blob.type || 'image/jpeg' });
  return uploadMediaFile(file, folder, nameHint || filename);
}
